import { useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import CautionIcon from '@/assets/icons/caution.svg';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  displayCautionIcon?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  type?: 'text' | 'email' | 'password' | 'number' | 'date';
  showPasswordToggle?: boolean;
  hint?: ReactNode | string;
  formatFunction?: (value: string) => string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  containerClassName?: string;
  labelClassName?: string;
  padding?: string;
  inputTextClassName?: string;
  rightIconClassName?: string;
}

export default function Input({
  label,
  error,
  displayCautionIcon,
  leftIcon,
  rightIcon,
  type = 'text',
  showPasswordToggle = false,
  className = '',
  containerClassName = '',
  labelClassName = '',
  name,
  hint,
  formatFunction,
  placeholder = '',
  onChange,
  padding,
  inputTextClassName,
  rightIconClassName,
  ...props
}: InputProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const isPassword = type === 'password';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (formatFunction) {
      value = formatFunction(value);
      e.target.value = value;
    }
    onChange?.(e);
  };

  return (
    <div className={clsx('flex flex-col w-full', className)}>
      {/* Outer container with fixed height */}
      <div className={clsx('relative w-full h-[62px] tablet:h-[77px]', containerClassName)}>
        {label && (
          <label
            htmlFor={name}
            className={clsx(
              'label absolute top-[10px] tablet:top-[13px] left-[10px] tablet:left-[14px] pb-[5px] tablet:pb-[6px]',
              labelClassName,
            )}
          >
            {label}
          </label>
        )}

        {leftIcon && (
          <div className="absolute left-3 top-0 bottom-0 flex items-center ">{leftIcon}</div>
        )}

        <input
          data-testid={name}
          id={name}
          name={name}
          className={clsx(
            'w-full h-full bg-paper rounded-[6px] tablet:rounded-[8px] border border-off-white focus:border-amberblack transition-all focus:outline-none text-body text-amberblack placeholder:text-silver',
            leftIcon
              ? 'pl-[46px] tablet:pl-[56px] pr-10'
              : 'px-[10px] tablet:px-[14px] pt-[30px] pb-[10px] tablet:pt-[38px] tablet:pb-[13px] leading-[22px]',
            padding && padding,
            inputTextClassName,
            isPassword && 'sentry-mask',
          )}
          type={isPassword ? (isPasswordVisible ? 'text' : 'password') : type}
          placeholder={placeholder}
          onChange={handleChange}
          {...props}
        />

        {error && displayCautionIcon ? (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <img src={CautionIcon} alt="error" className="w-5 h-5" />
          </div>
        ) : isPassword && showPasswordToggle ? (
          <button
            type="button"
            className="absolute right-3 bottom-[6px] tablet:bottom-0 -translate-y-1/2 cursor-pointer text-silver -mb-[9px] tablet:-mb-[4px]"
            onClick={() => setIsPasswordVisible((prev) => !prev)}
          >
            {isPasswordVisible ? (
              <>
                <Eye size={24} data-testid="eye-icon" className="tablet:hidden" />
                <Eye size={32} data-testid="eye-icon" className="hidden tablet:block" />
              </>
            ) : (
              <>
                <EyeSlash size={24} data-testid="eye-slash-icon" className="tablet:hidden" />
                <EyeSlash size={32} data-testid="eye-slash-icon" className="hidden tablet:block" />
              </>
            )}
          </button>
        ) : (
          rightIcon && (
            <div
              className={clsx(
                'absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none',
                rightIconClassName,
              )}
            >
              {rightIcon}
            </div>
          )
        )}
      </div>

      {error && (
        <p
          role="alert"
          className="text-body-sm text-[var(--color-error)] mt-[8px] ml-[4px] mb-[4px] tablet:mt-12 tablet:ml-8 tablet:mb-8"
        >
          {error}
        </p>
      )}

      {hint && (
        <p className="text-body-xs text-ashgray max-w-full w-[351px] tablet:w-full mt-[8px] ml-[4px] mb-[4px] tablet:mt-12 tablet:ml-8 tablet:mb-8">
          {hint}
        </p>
      )}
    </div>
  );
}
